import React, { useRef, useState } from "react";
import { Box, Grid, Button, CircularProgress } from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import ColorSwatch from "../../Swatch";
import { colors, gradients } from "../../../constants/background";

// NOTE: `fileUpload`, `helpers`, and `react-toastify` are imported DYNAMICALLY
// inside onFile (below), not statically at the top. `utils/fileUpload`
// transitively pulls in `heic2any` (which calls canvas.getContext at import
// time — unavailable in jsdom) and the large `services/api` barrel. Because the
// shared <ThemeEditor> statically imports THIS component even for its LinkVault
// (legacy) path, a static import here would drag that heavy chain into every
// test that renders ThemeEditor, breaking the LinkVault regression-pin suites.
// These deps are only needed on an actual image upload, so deferring them keeps
// the module graph light (and code-splits the heavy chain out of the initial
// bundle in production — a net webpack win). Runtime behavior is unchanged.

// Background token editor: none | solid color | gradient | uploaded image.
// value: { type, value }. onChange({type,value}); onImageExtract(url) fires
// after a successful image upload so the editor can live-extract a palette.
const BackgroundField = ({ value, onChange, onImageExtract }) => {
  const bg = value || {};
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const showError = async (msg) => {
      const { toast } = await import("react-toastify");
      toast.error(msg);
    };
    try {
      const { handleFileUpload, getUserProfilePath } = await import(
        "../../../utils/fileUpload"
      );
      const { getLoggedInUser } = await import("../../../utils/helpers");
      const userId = getLoggedInUser()?.id;
      const path = getUserProfilePath({ user_id: userId });
      const res = await handleFileUpload(file, path);
      if (res && res.location) {
        onChange({ type: "image", value: res.location });
        onImageExtract && onImageExtract(res.location);
      } else {
        await showError("Image upload failed.");
      }
    } catch (err) {
      console.error(err);
      await showError("Image upload failed.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <Box>
      <Grid container spacing={1} alignItems="center">
        <Grid item>
          <Box
            data-testid="bg-none"
            onClick={() => onChange({ type: "color", value: "" })}
            title="No background"
            sx={{
              width: 32, height: 32, display: "flex", alignItems: "center",
              justifyContent: "center", cursor: "pointer", borderRadius: "50%",
              border: !bg.value ? "1px solid #9B5AF8" : "1px solid transparent",
            }}
          >
            <BlockIcon sx={{ color: "#BBBBBB" }} />
          </Box>
        </Grid>
        {colors.map((c, i) => (
          <Grid item key={`c-${c}`}>
            <Box data-testid={`bg-color-${i}`} onClick={() => onChange({ type: "color", value: c })}>
              <ColorSwatch color={c} borderColor={bg.value === c && "#BBBBBB"} />
            </Box>
          </Grid>
        ))}
        {gradients.map((g, i) => (
          <Grid item key={`g-${g}`}>
            <Box data-testid={`bg-gradient-${i}`} onClick={() => onChange({ type: "gradient", value: g })}>
              <ColorSwatch color={g} borderColor={bg.value === g && "#BBBBBB"} />
            </Box>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 1 }}>
        <input
          ref={fileRef}
          data-testid="bg-image-input"
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={onFile}
        />
        <Button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          sx={{ textTransform: "none", fontSize: 13, color: "#9B5AF8" }}
        >
          {uploading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
          {bg.type === "image" && bg.value ? "Replace image" : "Upload image"}
        </Button>
        {bg.type === "image" && bg.value ? (
          <Box
            component="img"
            src={bg.value}
            alt="background preview"
            sx={{ display: "block", mt: 1, maxWidth: 220, borderRadius: 1 }}
          />
        ) : null}
      </Box>
    </Box>
  );
};

export default BackgroundField;
